import { SuccessfullAddEquipmentDialog } from '../components/equipment/successfullAddEquipmentDialog';
import { AddEquipmentDialog } from '../components/equipment/addEquipmentDialog';
import EquipmentPageHeader from '../components/equipment/equipmentPageHeader';
import EquipmentFiltration from '../components/equipment/equipmentFiltration';
import EquipmentList from '../components/equipment/equipmentList';
import EquipmentGrid from '../components/equipment/equipmentGrid';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, Box, LinearProgress } from '@mui/material';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const Equipments = () => {

    const hasEquipementCreatePermission = useHasPermission('equipment_create');

    const [openAddEquipement, setOpenAddEquipement] = useState(false);
    const [openSuccessfulAddEquipement, setOpenSuccessfulAddEquipement] = useState(false);

    const [loading, setLoading] = useState(true);
    const [equipements, setEquipements] = useState([])
    const [totalEquipements, setTotalEquipements] = useState(0);
    const [filteredEquipementsCount, setFilteredEquipementsCount] = useState('');
    const [equipementsFetched, setEquipementsFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('equipmentDisplayMode')) || 'grid');
    const defaultSortBy = {
        name: null,
        description: null,
    };

    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('equipementSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
    const [equipementsFilters, setEquipementsFilters] = useState(
        JSON.parse(localStorage.getItem('equipementsFilters')) ||
        { debouncedSearch: '' }
    );

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'equipments',
        pathname: '/dashboard/equipements',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: equipementsFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(equipementsFetched);

    useEffect(() => {
        setLoading(true)
    }, [equipementsFilters]);

    useEffect(() => {
        localStorage.setItem('equipementsFilters', JSON.stringify(equipementsFilters));
    }, [equipementsFilters]);

    useEffect(() => {
        localStorage.setItem('equipementSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('equipmentDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchEquipements = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('equipment/list', { params: { offset: adjustedOffset, limit, filters: equipementsFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedEquipements = [];
                // Update the fetched records in their correct positions
                response?.data?.equipments?.forEach((equipement, index) => { updatedEquipements[adjustedOffset + index] = equipement });
                // Update the state with the new equipment array
                setEquipements(updatedEquipements);
                setFilteredEquipementsCount(response?.data?.filteredEquipmentsCount);
                setTotalEquipements(response?.data?.totalEquipments);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching equipements:', error);
            CustomErrorLogger(error, 'fetch equipements in equipements page');
        }
    }, [equipementsFilters, offset, sortBy]);

    useEffect(() => {
        fetchEquipements()
    }, [fetchEquipements]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Équipement" list={true}>
            <EquipmentPageHeader
                setOpenAddEquipement={setOpenAddEquipement}
                hasEquipementCreatePermission={hasEquipementCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <EquipmentFiltration
                        filtersEmpty={filtersEmpty}
                        equipementsFilters={equipementsFilters}
                        setEquipementsFilters={setEquipementsFilters}
                        totalEquipements={totalEquipements}
                        equipementsFetched={equipementsFetched}
                        filteredEquipementsCount={filteredEquipementsCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalEquipements === 0 ?
                    <NoRecord
                        description="Aucun équipement ajouté pour le moment"
                        buttonTitle="Ajouter un équipement"
                        handleOpenDialog={() => setOpenAddEquipement(true)}
                        hasPermission={hasEquipementCreatePermission}
                    /> :
                    <Box>
                        <EquipmentFiltration
                            filtersEmpty={filtersEmpty}
                            equipementsFilters={equipementsFilters}
                            setEquipementsFilters={setEquipementsFilters}
                            totalEquipements={totalEquipements}
                            equipementsFetched={equipementsFetched}
                            filteredEquipementsCount={filteredEquipementsCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <EquipmentGrid
                                scrollRef={scrollRef}
                                equipements={equipements}
                                setOffset={setOffset}
                                filteredEquipementsCount={filteredEquipementsCount}
                                setEquipementsFetched={setEquipementsFetched}
                            />
                        ) : (
                            <EquipmentList
                                scrollRef={scrollRef}
                                equipements={equipements}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredEquipementsCount={filteredEquipementsCount}
                                setEquipementsFetched={setEquipementsFetched}
                            />
                        )}

                    </Box>
            }

            {openAddEquipement &&
                <AddEquipmentDialog
                    setEquipements={setEquipements}
                    setTotalEquipements={setTotalEquipements}
                    setFilteredEquipementsCount={setFilteredEquipementsCount}
                    openAddEquipement={openAddEquipement}
                    setOpenAddEquipement={setOpenAddEquipement}
                />
            }

            {openSuccessfulAddEquipement &&
                <SuccessfullAddEquipmentDialog
                    openSuccessfulAddEquipement={openSuccessfulAddEquipement}
                    setOpenAddEquipement={setOpenAddEquipement}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Equipments;